<script lang="ts">
  import BackToDatasetButton from "./BackToDatasetButton.svelte";
  import {
    stationsSummary,
    resultSummary,

    StationsSummary,

    displayStations,
    selectedStations,
    highlightedStation,
    isLoading,
    filters,
    coordinateInput,
    hasActiveFilters,
    isSpatiallyFiltered,
    totalStationsCount,
    filterActions,
    selectionActions,
    focusedStation
  } from "../../services/station-service";
  import StationFacets from "./StationFacets.svelte";
  import { derived, type Readable } from "svelte/store";

  const summary: Readable<StationsSummary> = derived([stationsSummary, resultSummary], ([stations, result]): StationsSummary => {
    return {
      datasets: result ? result?.datasets : stations?.datasets,
      networks: result ? result?.networks : stations?.networks,
      startDate: result ? result?.startDate : stations?.startDate,
      endDate: result ? result?.endDate : stations?.endDate,
    }
  })

  export let width = 30;

  function formatDateRange(beginDate, endDate) {
    const format = (dateStr) => {
      if (!dateStr || dateStr === "Present" || dateStr === "Unknown") {
        return dateStr;
      }

      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime()) || date.getFullYear() < 1800) {
          return "Unknown";
        }

        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short"
        });
      } catch (e) {
        return "Unknown";
      }
    };

    return `${format(beginDate)} - ${format(endDate)}`;
  }

  function selectStation(stationId) {
    const station = $displayStations.find((s) => s.id === stationId);
    if (station) focusedStation.set(station);
  }

  function handleStationKeydown(event, stationId) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectStation(stationId);
    }
  }

  function handleNameInput(event) {
    filterActions.updateFilters({ name: event.target.value });
  }

  function handleIdInput(event) {
    filterActions.updateFilters({ id: event.target.value });
  }

  function handleLatInput(event) {
    filterActions.searchByCoordinates(event.target.value, $coordinateInput.lng);
  }

  function handleLngInput(event) {
    filterActions.searchByCoordinates($coordinateInput.lat, event.target.value);
  }

  function handleDateStartChange(event) {
    filterActions.setDateRange(event.target.value, $filters.dateEnd);
  }

  function handleDateEndChange(event) {
    filterActions.setDateRange($filters.dateStart, event.target.value);
  }

  function handleDatasetChange(event) {
    filterActions.updateFilters({ datasetId: event.target.value });
  }

  function handleNetworkChange(event) {
    filterActions.updateFilters({ networkId: event.target.value });
  }

</script>

<div class="station-sidebar" style="--sidebar-width: {width}%">
  <div class="station-list-header">
    <div class="d-flex justify-content-between align-items-center">
      <h3>Stations</h3>
      <div class="d-flex align-items-center">
        {#if $isLoading}
          <div class="spinner-border spinner-border-sm text-secondary me-2" role="status">
          <span class="visually-hidden">Loading...</span>
          </div>
        {/if}
        <span class="badge bg-secondary">{$displayStations.length.toLocaleString()} found</span>
      </div>
    </div>

    {#if $isSpatiallyFiltered}
      <div class="alert alert-info alert-sm mt-2">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <i class="bi bi-geo-alt-fill me-2"></i>
            <strong>Spatial filter active</strong>
            <div class="small text-muted">
              Showing {$displayStations.length.toLocaleString()} of {$totalStationsCount.toLocaleString()}
              stations
            </div>
          </div>
          <button
            class="btn btn-sm btn-outline-info"
            on:click={() => filterActions.clearSpatialFilter()}
            title="Clear spatial filter"
            aria-label="Clear spatial filter"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>
    {/if}

    <div class="filters mt-2">
      {#if $summary?.datasets?.length > 0}
        <div class="filter-group">
          <select
            id="dataset-select"
            class="form-select form-select-sm"
            value={$filters.datasetId}
            on:change={handleDatasetChange}
            aria-label="Filter by dataset"
          >
            <option value="">All Datasets</option>
            {#each $summary?.datasets as dataset}
              <option value={dataset.id}>{dataset.name}</option>
            {/each}
          </select>
        </div>
      {/if}
      {#if $summary?.networks?.length > 0}
        <div class="filter-group mt-2">
          <select
            id="network-select"
            class="form-select form-select-sm"
            value={$filters.networkId}
            on:change={handleNetworkChange}
            aria-label="Filter by network"
          >
            <option value="">All Networks</option>
            {#each $summary?.networks as network}
              <option value={network}>{network}</option>
            {/each}
          </select>
        </div>
      {/if}

      <div class="filter-group mt-2">
        <input
          id="name-input"
          type="text"
          class="form-control form-control-sm"
          placeholder="Station Name (min 2 chars)"
          value={$filters.name}
          on:input={handleNameInput}
          aria-label="Filter by station name"
        />
      </div>

      <div class="filter-group mt-2">
        <input
          id="id-input"
          type="text"
          class="form-control form-control-sm"
          placeholder="Station ID (e.g., GHCND:USW00094728)"
          value={$filters.id}
          on:input={handleIdInput}
          aria-label="Filter by station ID"
        />
      </div>

      <div class="filter-group mt-2">
        <label class="form-label small text-muted mb-1" for="lat-input">Coordinates</label>
        <div class="row g-1">
          <div class="col-6">
            <input
              id="lat-input"
              type="text"
              inputmode="decimal"
              class="form-control form-control-sm"
              placeholder="Latitude (e.g., 40.7128)"
              value={$coordinateInput.lat}
              on:input={handleLatInput}
              aria-label="Filter by latitude"
            />
          </div>
          <div class="col-6">
            <input
              id="lng-input"
              type="text"
              inputmode="decimal"
              class="form-control form-control-sm"
              placeholder="Longitude (e.g., -74.0060)"
              value={$coordinateInput.lng}
              on:input={handleLngInput}
              aria-label="Filter by longitude"
            />
          </div>
        </div>
      </div>

      <div class="filter-group mt-2">
        <label class="form-label small text-muted mb-1">Date Range</label>
        <div class="row g-1">
          <div class="col-6">
            <input
              type="date"
              class="form-control form-control-sm"
              value={$filters.dateStart}
              min={$summary?.startDate}
              max={$summary?.endDate}
              on:change={handleDateStartChange}
              aria-label="Start date"
            />
          </div>
          <div class="col-6">
            <input
              type="date"
              class="form-control form-control-sm"
              value={$filters.dateEnd}
              min={$summary?.startDate}
              max={$summary?.endDate}
              on:change={handleDateEndChange}
              aria-label="End date"
            />
          </div>
        </div>
      </div>

      <button
        class="btn btn-sm btn-outline-secondary w-100 mt-3"
        on:click={() => filterActions.resetFilters()}
        disabled={!$hasActiveFilters}
      >
        <i class="bi bi-arrow-counterclockwise me-1"></i>
        Reset Filters
      </button>
    </div>

    {#if $selectedStations.length > 0}
      <div class="selection-summary alert alert-success alert-sm mt-2">
        <div class="d-flex justify-content-between align-items-center">
          <small>
            <i class="bi bi-check-circle-fill me-1"></i>
            {$selectedStations.length} station{$selectedStations.length !== 1 ? "s" : ""} selected
          </small>
          <button
            class="btn btn-sm btn-outline-danger"
            on:click={() => selectionActions.clear()}
            title="Clear selection"
            aria-label="Clear all selections"
          >
            <i class="bi bi-x"></i>
          </button>
        </div>
      </div>
    {/if}
  </div>

  <div
    class="station-list"
    role="list"
    aria-label="Station list"
  >
    {#if $isLoading && $displayStations.length === 0}
      <div class="text-center p-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <div class="mt-2">
          <strong>Loading stations...</strong>
          <div class="small text-muted">Fetching stations for current map view</div>
        </div>
      </div>
    {:else if $displayStations.length === 0}
      <div class="empty-state">
        <i class="bi bi-geo-alt"></i>
        <h5>No Stations Found</h5>
        <p class="text-muted">
          {#if $isSpatiallyFiltered}
            No stations in the selected area. Try adjusting your shape.
          {:else if $hasActiveFilters}
            No stations match your filters. Try adjusting your search criteria.
          {:else}
            Pan or zoom the map to load stations in this area.
          {/if}
        </p>
      </div>
    {:else}
      {#each $displayStations as station (station.id)}
        <div
          class="station-item"
          class:highlighted={$highlightedStation === station.id}
          class:selected={$selectedStations.includes(station.id)}
          on:click={() => selectStation(station.id)}
          on:keydown={(e) => handleStationKeydown(e, station.id)}
          role="listitem"
          aria-label="Station: {station.name}"
        >
          <div class="station-header">
            <h5 title={station.name}>{station.name}</h5>
            {#if $selectedStations.includes(station.id)}
              <div class="selection-badge" aria-label="Selected">
                <i class="bi bi-check-circle-fill"></i>
              </div>
            {/if}
          </div>

          <div class="station-details">

            <!-- otherNames / aliases -->
            <StationFacets
              facets={station?.otherNames?.filter(f => f !== station.name).map(a => {return {type: a, value: null}})}
              facetCategory="aliases"
              {station}
            />

            <!-- Datasets -->
            <StationFacets
              facets={station?.datasets?.map(d => {return {type: d.name, value: null}})}
              facetCategory="datasets"
              {station}
            />

            <!-- Identifiers -->
            <StationFacets
              facets={station?.identifiers?.map(i => {return {type: i.idType, value: i.id}})}
              facetCategory="identifiers"
              {station}
            />

            <!-- Date Range -->
            <div class="station-date">
              <i class="bi bi-calendar me-1" aria-hidden="true"></i>
              <span>{formatDateRange(station.beginDate, station.endDate)}</span>
            </div>

            <!-- Coordinates -->
            <div class="station-coords">
              <i class="bi bi-geo me-1" aria-hidden="true"></i>
              <span>{station.latitude.toFixed(4)}, {station.longitude.toFixed(4)}</span>
            </div>
          </div>

          <div class="station-actions">
            <a
              href="station-detail?stations={station.id}"
              class="btn btn-sm btn-outline-primary detail-link"
              title="View station detail page"
              aria-label="View details for {station.name}"
            >
              <i class="bi bi-arrow-right-circle"></i> View Details
            </a>
            <button
              class="btn btn-sm"
              class:btn-success={$selectedStations.includes(station.id)}
              class:btn-outline-success={!$selectedStations.includes(station.id)}
              on:click={(e) => {
                    e.stopPropagation();
                    selectionActions.toggle(station.id);
                    focusedStation.set(station);
                  }}
              aria-pressed={$selectedStations.includes(station.id)}
            >
              {#if $selectedStations.includes(station.id)}
                <i class="bi bi-check2"></i> Selected
              {:else}
                <i class="bi bi-plus"></i> Select
              {/if}
            </button>
          </div>
        </div>
      {/each}
    {/if}
  </div>

  <div class="sidebar-footer">
    <BackToDatasetButton />
  </div>
</div>

<style>
  .station-sidebar {
    width: var(--sidebar-width, 30%);
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: #f8f9fa;
    border-right: 1px solid #dee2e6;
  }

  .station-list-header {
    padding: 12px;
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
    flex-shrink: 0;
  }

  .station-list-header h3 {
    margin: 0;
    font-size: 1.25rem;
  }

  .station-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
    height: 600px;
  }

  .sidebar-footer {
    padding: 10px;
    background-color: #f8f9fa;
    border-top: 1px solid #dee2e6;
    flex-shrink: 0;
  }

  .alert-sm {
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
  }

  .filter-group {
    margin-bottom: 0;
  }

  .filter-group .form-label {
    margin-bottom: 0.25rem;
    font-weight: 500;
  }

  .filters input:focus,
  .filters select:focus {
    border-color: #007ec6;
    box-shadow: 0 0 0 0.2rem rgba(0, 126, 198, 0.15);
  }

  .empty-state {
    text-align: center;
    padding: 2rem 1rem;
    color: #6c757d;
  }

  .empty-state i {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  .empty-state h5 {
    margin-bottom: 0.5rem;
    color: #495057;
  }

  .empty-state p {
    font-size: 0.875rem;
    margin: 0;
  }

  .station-item {
    background-color: white;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 8px;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .station-item:hover {
    background-color: #f8f9fa;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .station-item:focus {
    outline: 2px solid #007ec6;
    outline-offset: 2px;
  }

  .station-item.highlighted {
    background-color: #e6f7ff;
    border-color: #007bff;
    border-left-width: 3px;
  }

  .station-item.selected {
    background-color: #fff8f5;
    border-color: #c9520f;
    border-left-width: 3px;
  }

  .station-item.highlighted.selected {
    border-color: #c9520f;
  }

  .station-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  .station-header h5 {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0;
    color: #212529;
    line-height: 1.3;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 8px;
  }

  .selection-badge {
    color: #c9520f;
    font-size: 1rem;
    flex-shrink: 0;
  }

  .station-details {
    font-size: 0.8rem;
    color: #6c757d;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    margin-bottom: 8px;
    overflow: hidden;
  }

  .station-details > div {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    line-height: 1.4;
  }

  .station-details i {
    width: 14px;
    flex-shrink: 0;
    font-size: 0.75rem;
    margin-top: 2px;
  }

  .station-date span,
  .station-coords span {
    color: #6c757d;
  }

  .station-actions {
    display: flex;
    flex-direction: row;align-self: flex-end;
    gap: 6px;
    align-items: center;
  }

  .station-actions .btn {
    font-size: 0.75rem;
    padding: 5px 10px;
    border-radius: 4px;
    font-weight: 500;
  }

  .selection-summary {
    background-color: #fdf0e8;
    border-color: #c9520f;
    color: #6b2808;
  }

  @media (max-width: 991px) {
    .station-item {
      height: auto !important;
      min-height: 160px;
    }

    .station-header h5 {
      white-space: normal;
    }
  }

  @media (max-width: 767px) {
    .station-sidebar {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid #dee2e6;
    }

    .station-list {
      max-height: 300px;
    }

    .station-item {
      min-height: 140px;
    }

    .station-header h5 {
      font-size: 0.9rem;
    }

    .station-details {
      font-size: 0.75rem;
    }

    .filters .form-control,
    .filters .form-select {
      font-size: 16px;
    }

    .filters {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }

    .filters .filter-group:has(select),
    .filters .filter-group:has(.row),
    .filters button {
      grid-column: 1 / -1;
    }
  }

  :global(.btn-success),
  :global(.btn-outline-success) {
    --bs-btn-focus-shadow-rgb: 201, 82, 15;
  }
  :global(.btn-success) {
    background-color: #c9520f;
    border-color: #c9520f;
    color: #fff;
  }
  :global(.btn-success:hover),
  :global(.btn-success:focus),
  :global(.btn-success:active) {
    background-color: #a8430c;
    border-color: #a8430c;
    color: #fff;
    box-shadow: 0 0 0 0.25rem rgba(201, 82, 15, 0.4);
  }
  :global(.btn-outline-success) {
    color: #c9520f;
    border-color: #c9520f;
  }
  :global(.btn-outline-success:hover),
  :global(.btn-outline-success:focus),
  :global(.btn-outline-success:active) {
    background-color: #c9520f;
    border-color: #c9520f;
    color: #fff;
    box-shadow: 0 0 0 0.25rem rgba(201, 82, 15, 0.4);
  }
  :global(.alert-success) {
    background-color: #fdf0e8;
    border-color: #c9520f;
    color: #6b2808;
  }
</style>